import { useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Mic, MicOff, Volume2, VolumeX, ArrowLeft, RotateCcw, MessageSquare, Sparkles, AlertCircle, X } from "lucide-react";
import { useSpeechRecognition, useSpeechSynthesis } from "../hooks/useSpeech";
import { getScenarioById, getTopicById } from "../data/multiLanguage";
import { getSmartResponse, resetContext, SCENARIOS } from "../conversationEngine";
import { earnXP } from "../gamification";
import LanguageChip from "../components/LanguageChip";
import { createSession, appendMessage, endSession } from "../lib/sessionStore";
import { startActivityTracking, stopActivityTracking } from "../lib/dailyPlan";

// Map speakeasy scenario IDs → existing conversation engine scenario IDs.
const SCENARIO_MAP = {
  "job-interview": "interview",
  "restaurant-order": "restaurant",
  "airport-checkin": "travel",
  "business-meeting": "office",
  "doctor-visit": "medical",
  "phone-call": "phone",
  "ielts-speaking-part1": "interview",
  "ielts-speaking-part2": "interview",
  "ielts-speaking-part3": "interview",
  "bank-visit": "bank",
  "shopping-clothes": "shopping",
  "small-talk": "daily",
};

const TOPIC_MAP = {
  technology: "techsupport",
  travel: "travel",
  "food-culture": "restaurant",
  "current-events": "daily",
  sports: "fitness",
  "movies-entertainment": "daily",
  education: "academic",
  "work-career": "office",
  environment: "daily",
  "health-fitness": "fitness",
  "family-relationships": "daily",
  "money-finance": "bank",
};

export default function SpeakPractice() {
  const [params] = useSearchParams();
  const scenarioId = params.get("scenario");
  const topicId = params.get("topic");

  const config = scenarioId
    ? { type: "scenario", data: getScenarioById(scenarioId), engineKey: SCENARIO_MAP[scenarioId] || "daily" }
    : topicId
    ? { type: "topic", data: getTopicById(topicId), engineKey: TOPIC_MAP[topicId] || "daily" }
    : { type: "free", data: null, engineKey: "daily" };

  const [messages, setMessages] = useState([]);
  const [autoSpeak, setAutoSpeak] = useState(true);
  const [showHelp, setShowHelp] = useState(false);
  const [exchangeCount, setExchangeCount] = useState(0);
  const chatEndRef = useRef(null);
  const sessionIdRef = useRef(null);

  const { transcript, interim, listening, error, start, stop, reset, supported: micSupported } = useSpeechRecognition({
    lang: "en-US",
    continuous: false,
    interimResults: true,
  });

  const { speak, cancel, speaking } = useSpeechSynthesis();

  // Greet user once on mount + create new session
  useEffect(() => {
    resetContext();
    const greeting = config.data?.openingLine || SCENARIOS[config.engineKey]?.greeting ||
      "Hi! Let's practice English together. What would you like to talk about today?";
    const session = createSession({
      mode: config.type,
      title: config.data?.title || "Free conversation",
      scenarioId,
      topicId,
      openingLine: greeting,
    });
    sessionIdRef.current = session.id;
    setMessages([{ role: "ai", text: greeting, time: Date.now() }]);
    if (autoSpeak) {
      const t = setTimeout(() => speak(greeting, { lang: "en-US", rate: 0.9 }), 600);
      return () => clearTimeout(t);
    }
    return () => {
      if (sessionIdRef.current) endSession(sessionIdRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // End session when window unloads
  useEffect(() => {
    const onUnload = () => {
      if (sessionIdRef.current) endSession(sessionIdRef.current);
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, []);

  // Track oral practice minutes (counts toward daily 1hr oral target)
  useEffect(() => {
    startActivityTracking("oral", "speak-practice");
    return () => stopActivityTracking();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, interim]);

  const handleUserMessage = useCallback(
    (userText) => {
      if (!userText.trim()) return;
      const newCount = exchangeCount + 1;
      setExchangeCount(newCount);

      const userMsg = { role: "user", text: userText, time: Date.now() };
      setMessages((prev) => [...prev, userMsg]);
      if (sessionIdRef.current) appendMessage(sessionIdRef.current, userMsg);

      // Get AI response from local engine (FREE — no API cost)
      setTimeout(() => {
        const response = getSmartResponse(config.engineKey, userText, newCount);
        // Extract correction tip if present (engine appends "💡 Tip: ...")
        const tipMatch = response.match(/\n\n(💡 Tip:.+)$/);
        const aiText = tipMatch ? response.replace(tipMatch[0], "") : response;
        const correction = tipMatch ? tipMatch[1].replace("💡 Tip: ", "") : null;

        const aiMsg = { role: "ai", text: aiText, correction, time: Date.now() };
        setMessages((prev) => [...prev, aiMsg]);
        if (sessionIdRef.current) appendMessage(sessionIdRef.current, aiMsg);

        if (autoSpeak) speak(aiText, { lang: "en-US", rate: 0.9 });
        earnXP(5, "speaking");
      }, 500 + Math.random() * 400);
    },
    [config.engineKey, exchangeCount, autoSpeak, speak]
  );

  // Auto-submit when user stops talking and we have a final transcript.
  useEffect(() => {
    if (!listening && transcript && transcript.trim()) {
      handleUserMessage(transcript);
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listening, transcript]);

  function toggleMic() {
    if (speaking) cancel();
    if (listening) {
      stop();
    } else {
      start();
    }
  }

  function restartConversation() {
    cancel();
    stop();
    reset();
    resetContext();
    setExchangeCount(0);
    setMessages([]);
    const greeting = config.data?.openingLine || SCENARIOS[config.engineKey]?.greeting ||
      "Let's start fresh. What would you like to talk about?";
    setMessages([{ role: "ai", text: greeting, time: Date.now() }]);
    if (autoSpeak) setTimeout(() => speak(greeting, { lang: "en-US", rate: 0.9 }), 400);
  }

  const title = config.data?.title || "Free Conversation";
  const subtitle = config.data?.description || "Open-ended chat with AI partner";

  return (
    <div className="page-enter full-bleed -mt-6 min-h-[calc(100vh-4rem)] flex flex-col bg-paper">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-white/85 dark:bg-ink-900/85 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 lg:px-8 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
          <Link to={config.type === "scenario" ? "/scenarios" : config.type === "topic" ? "/topics" : "/dashboard"} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-ink-800 transition">
            <ArrowLeft className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          </Link>
          <div className="flex-1 min-w-0 text-center">
            <h1 className="font-bold text-base text-gray-900 dark:text-ink-900 truncate">{title}</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{subtitle}</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setAutoSpeak(!autoSpeak)}
              className={`p-2 rounded-full transition ${autoSpeak ? "bg-sage-light dark:bg-sage-dark/40 text-sage-dark" : "bg-gray-100 dark:bg-ink-800 text-gray-400"}`}
              title={autoSpeak ? "Voice ON" : "Voice OFF"}
            >
              {autoSpeak ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <button
              onClick={restartConversation}
              className="p-2 rounded-full bg-gray-100 dark:bg-ink-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 transition"
              title="Restart"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Browser-not-supported warning */}
      {!micSupported && (
        <div className="max-w-4xl mx-auto w-full px-4 mt-4">
          <div className="flex items-start gap-3 p-4 rounded-2xl bg-gold-50 dark:bg-gold-700/20 border border-gold-300 dark:border-gold-500">
            <AlertCircle className="w-5 h-5 text-gold-700 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-gold-700 dark:text-gold-300 text-sm">Voice not supported in this browser</p>
              <p className="text-xs text-gold-700 dark:text-gold-300 mt-1">Please use Chrome or Edge for the best experience. You can still type your responses.</p>
            </div>
          </div>
        </div>
      )}

      {/* Topic guiding questions (if topic mode) */}
      {config.type === "topic" && config.data?.guidingQuestions && (
        <div className="max-w-4xl mx-auto w-full px-4 mt-4">
          <details className="rounded-2xl bg-gold-100 dark:bg-gold-500/20 border border-gold-300 dark:border-gold-500 overflow-hidden">
            <summary className="px-4 py-3 cursor-pointer font-semibold text-sm text-gold-700 dark:text-paper flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Need ideas? Try these questions
            </summary>
            <div className="px-4 pb-4 space-y-2">
              {config.data.guidingQuestions.map((q, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-gold-700 dark:text-paper">
                  <span className="font-bold text-gold-700">{i + 1}.</span>
                  <span>{q}</span>
                </div>
              ))}
            </div>
          </details>
        </div>
      )}

      {/* Messages */}
      <main className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="max-w-4xl mx-auto space-y-4">
          {messages.map((m, i) => (
            <MessageBubble key={i} message={m} onReplay={(text) => speak(text, { lang: "en-US", rate: 0.9 })} />
          ))}
          {/* Interim transcript while user speaks */}
          {listening && interim && (
            <div className="flex justify-end">
              <div className="max-w-[80%] rounded-2xl rounded-tr-sm px-4 py-2.5 bg-cream-200 dark:bg-ink-900 text-gold-700 dark:text-paper italic opacity-70">
                {interim}…
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
      </main>

      {/* Mic button */}
      <footer className="sticky bottom-0 backdrop-blur-md bg-white/85 dark:bg-ink-900/85 border-t border-gray-200 dark:border-gray-700 px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-center gap-4">
          <LanguageChip className="hidden sm:inline-flex" />

          <button
            onClick={toggleMic}
            disabled={!micSupported}
            className={`relative w-20 h-20 rounded-full shadow-2xl transition-all duration-300 flex items-center justify-center ${
              listening
                ? "bg-gradient-to-br from-berry-light0 to-rust-dark scale-110 animate-pulse"
                : speaking
                ? "bg-gradient-to-br from-sage to-sage-dark"
                : "bg-gradient-to-br from-ink-900 via-gold-500 to-gold-600 hover:scale-105"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            {listening && <div className="absolute inset-0 rounded-full bg-berry/40 animate-ping" />}
            {listening ? <MicOff className="w-9 h-9 text-ink-900" /> : <Mic className="w-9 h-9 text-ink-900" />}
          </button>

          <button
            onClick={() => setShowHelp(true)}
            className="p-2 rounded-full bg-gray-100 dark:bg-ink-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 transition"
            title="Help"
          >
            <MessageSquare className="w-5 h-5" />
          </button>
        </div>
        <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-3">
          {listening ? "🎙️ Listening — tap to stop" : speaking ? "🔊 AI speaking…" : "Tap mic and speak in English"}
        </p>
        {error && <p className="text-center text-xs text-berry mt-1">{error}</p>}
      </footer>

      {/* Help modal */}
      {showHelp && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowHelp(false)}>
          <div className="bg-white dark:bg-ink-900 rounded-3xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-ink-900">How it works</h3>
              <button onClick={() => setShowHelp(false)} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-ink-800">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <ol className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
              <li className="flex gap-3"><span className="font-bold text-gold-700 shrink-0">1.</span> Tap the big mic button and speak naturally in English.</li>
              <li className="flex gap-3"><span className="font-bold text-gold-700 shrink-0">2.</span> The AI partner replies — listen carefully and look for the 💡 grammar tip.</li>
              <li className="flex gap-3"><span className="font-bold text-gold-700 shrink-0">3.</span> Tap any AI message to replay the audio.</li>
              <li className="flex gap-3"><span className="font-bold text-gold-700 shrink-0">4.</span> Earn 5 XP each time you speak. Build streaks!</li>
            </ol>
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              ⚡ Voice runs entirely in your browser using Web Speech API — no audio leaves your device.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// MessageBubble + Correction card
// ============================================================
function MessageBubble({ message, onReplay }) {
  const isAi = message.role === "ai";
  return (
    <div className={`flex ${isAi ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[85%] sm:max-w-[80%] flex flex-col gap-2 ${isAi ? "items-start" : "items-end"}`}>
        <div className={`group relative px-4 py-3 rounded-2xl shadow-sm transition-all ${
          isAi
            ? "bg-white dark:bg-ink-800 rounded-tl-sm border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-ink-900"
            : "bg-gradient-to-br from-ink-900 to-gold-600 rounded-tr-sm text-ink-900"
        }`}>
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.text}</p>
          {isAi && (
            <button
              onClick={() => onReplay(message.text)}
              className="absolute -bottom-2 -right-2 w-7 h-7 rounded-full bg-sage text-ink-900 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
              title="Replay"
            >
              <Volume2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Correction card */}
        {isAi && message.correction && (
          <div className="ml-4 px-4 py-2.5 rounded-2xl bg-gradient-to-r from-gold-100 to-gold-100 dark:from-gold-700/30 dark:to-gold-700/30 border border-gold-300 dark:border-gold-500 max-w-full">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-gold-700 shrink-0 mt-0.5" />
              <p className="text-xs text-gold-700 dark:text-gold-300 leading-relaxed">
                <span className="font-bold">Tip:</span> {message.correction}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
